import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Search, LayoutGrid, List as ListIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  PLANNING_STATUSES,
  PLANNED_FORMATS,
  PRIORITIES,
  VIGENCIA_OPTIONS,
  usePlanningItems,
  useDeletePlanningItem,
  type PlanningItem,
} from '@/hooks/useContentPlanning';
import { ContentPlanningKanban } from './ContentPlanningKanban';
import { ContentPlanningTable } from './ContentPlanningTable';
import { ContentPlanningDialog } from './ContentPlanningDialog';
import { ContentPlanningPreviewDialog } from './ContentPlanningPreviewDialog';

export function ContentPlanningPanel() {
  const { data: items = [], isLoading } = usePlanningItems();
  const deleteMut = useDeletePlanningItem();

  const [view, setView] = useState<'kanban' | 'table'>('kanban');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [formatFilter, setFormatFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [responsibleFilter, setResponsibleFilter] = useState<string>('all');
  const [vigenciaFilter, setVigenciaFilter] = useState<string>('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PlanningItem | null>(null);
  const [deleting, setDeleting] = useState<PlanningItem | null>(null);
  const [previewing, setPreviewing] = useState<PlanningItem | null>(null);

  const responsibles = useMemo(() => {
    const set = new Set<string>();
    items.forEach((i) => i.responsible && set.add(i.responsible));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (statusFilter !== 'all' && it.planning_status !== statusFilter) return false;
      if (formatFilter !== 'all' && it.planned_format !== formatFilter) return false;
      if (priorityFilter !== 'all' && it.priority !== priorityFilter) return false;
      if (responsibleFilter !== 'all' && it.responsible !== responsibleFilter) return false;
      if (vigenciaFilter !== 'all' && (it.vigencia ?? 'vigente') !== vigenciaFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${it.name} ${it.description ?? ''} ${it.category ?? ''} ${it.prospect_route ?? ''} ${it.observations ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, statusFilter, formatFilter, priorityFilter, responsibleFilter, vigenciaFilter]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    items.forEach((i) => (c[i.planning_status] = (c[i.planning_status] ?? 0) + 1));
    return c;
  }, [items]);

  const handleEdit = (item: PlanningItem) => {
    setEditing(item);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMut.mutateAsync(deleting.id);
      toast.success('Contenido eliminado');
      setDeleting(null);
    } catch (e: any) {
      toast.error(e?.message ?? 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">Planificación de Contenidos</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Gestiona el pipeline editorial. Solo los contenidos en estado <strong>Publicado</strong> se muestran a los usuarios finales.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border p-0.5 bg-muted/30">
            <Button
              size="sm"
              variant={view === 'kanban' ? 'default' : 'ghost'}
              className="h-8"
              onClick={() => setView('kanban')}
            >
              <LayoutGrid className="h-4 w-4 mr-1.5" /> Kanban
            </Button>
            <Button
              size="sm"
              variant={view === 'table' ? 'default' : 'ghost'}
              className="h-8"
              onClick={() => setView('table')}
            >
              <ListIcon className="h-4 w-4 mr-1.5" /> Tabla
            </Button>
          </div>
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4 mr-1.5" /> Nuevo planificado
          </Button>
        </div>
      </div>

      {/* Stats por estado */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {PLANNING_STATUSES.map((s) => (
          <Card key={s.value}>
            <CardHeader className="pb-1">
              <CardTitle className="text-xs font-medium text-muted-foreground">{s.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{counts[s.value] ?? 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, ruta, observaciones..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {PLANNING_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Prioridad" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las prioridades</SelectItem>
            {PRIORITIES.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={formatFilter} onValueChange={setFormatFilter}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="Formato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los formatos</SelectItem>
            {PLANNED_FORMATS.map((f) => (
              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={vigenciaFilter} onValueChange={setVigenciaFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Vigencia" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las vigencias</SelectItem>
            {VIGENCIA_OPTIONS.map((v) => (
              <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {responsibles.length > 0 && (
          <Select value={responsibleFilter} onValueChange={setResponsibleFilter}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Responsable" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los responsables</SelectItem>
              {responsibles.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        Mostrando {filtered.length} de {items.length} contenidos
      </p>

      {/* Vista */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Cargando...</div>
      ) : view === 'kanban' ? (
        <ContentPlanningKanban
          items={filtered}
          onEdit={handleEdit}
          onDelete={setDeleting}
          onPreview={setPreviewing}
        />
      ) : (
        <ContentPlanningTable
          items={filtered}
          onEdit={handleEdit}
          onDelete={setDeleting}
          onPreview={setPreviewing}
        />
      )}

      <ContentPlanningPreviewDialog
        item={previewing}
        open={!!previewing}
        onOpenChange={(o) => !o && setPreviewing(null)}
      />

      {/* Dialogs */}
      {dialogOpen && (
        <ContentPlanningDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          item={editing}
        />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este contenido planificado?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>"{deleting?.name}"</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
